import {
  ThemeProvider,
  createTheme,
  ThemeOptions,
  Box,
  Typography,
  Button,
  responsiveFontSizes,
  Dialog,
  DialogContent,
  Link,
  Stack,
  IconButton,
} from '@mui/material'
import '@mui/material/styles'
import { CloseRounded } from '@mui/icons-material'
import { ErrorBoundary } from 'react-error-boundary'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fsColors } from '../../shared/constants'
import { logoutUser, removeAccessReq } from '../../store/actions'
import { ReactComponent as ErrorLogo } from '../../assets/error_404.svg'
import { ReactComponent as FSLogo } from '../../assets/logos/FAIR_solution.svg'
import { ReactComponent as PTLogo } from '../../assets/logos/Pay_transparency.svg'
import { ReactComponent as FSLogoDark } from '../../assets/Logo_FAIR_dark.svg'
import { ReactComponent as PTLogoDark } from '../../assets/logos/PT_dark.svg'
import RalewayMedium from '../../fonts/RalewayMedium.ttf'
import RalewayItalic from '../../fonts/RalewayItalic.ttf'
import RalewayBold from '../../fonts/RalewayBold.ttf'

type Props = {
  children: React.ReactNode
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    n1: React.CSSProperties
    n2: React.CSSProperties
    n3: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    n1?: React.CSSProperties
    n2?: React.CSSProperties
    n3?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    n1: true
    n2: true
    n3: true
  }
}

const {
  fsPurple,
  fsBlue,
  fsDarkBlue,
  fsMagenta,
  fsLightGreen,
  fsLightOrange,
  fsGreen,
  fsLightBlue,
  fsOrange,
  fsGray,
} = fsColors

const Fallback = ({ error, resetErrorBoundary }: { error: any; resetErrorBoundary: any }) => {
  const { t } = useTranslation()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
    >
      <ErrorLogo height="150px" />
      <Typography variant="h6" mt={2} color={fsDarkBlue.primary}>
        {t('something.went.wrong.small')}
      </Typography>
      <Typography variant="caption">{t('no.access.request')}</Typography>

      <Typography color={fsGray.colors[4]} mt={2}>{`${t('error.message')} ${
        error.message
      }`}</Typography>
      <Button variant="contained" onClick={resetErrorBoundary} sx={{ mt: 4 }}>
        {t('refresh.app')}
      </Button>
    </Box>
  )
}

const FsTheme = ({ children }: Props) => {
  const { currentTheme, askAccess, currentLanguage, htmlFontSize } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const commonThemeOptions = {
    paper: {
      backgroundImage: 'none',
    },
    fsPurple: {
      main: fsPurple.primary,
      contrastText: '#fff',
    },
    fsBlue: {
      main: fsBlue.primary,
      contrastText: '#fff',
    },
    fsDarkBlue: {
      main: fsDarkBlue.primary,
      contrastText: '#fff',
    },
    fsMagenta: {
      main: fsMagenta.primary,
      contrastText: '#fff',
    },
    fsLightGreen: {
      main: fsLightGreen.primary,
      contrastText: '#fff',
    },
    fsLightOrange: {
      main: fsLightOrange.primary,
      contrastText: '#fff',
    },
    fsGreen: {
      main: fsGreen.primary,
      contrastText: '#fff',
    },
    fsLightBlue: {
      main: fsLightBlue.primary,
      contrastText: '#fff',
    },
    fsOrange: {
      main: fsOrange.primary,
      contrastText: '#fff',
    },
    fsGray: {
      main: fsGray.primary,
      contrastText: '#fff',
    },
  }

  const lightThemeOptions: ThemeOptions = {
    palette: {
      mode: 'light',
      primary: {
        main: fsColors.fsDarkBlue.colors[0],
        dark: fsColors.fsDarkBlue.colors[0],
        light: fsColors.fsDarkBlue.colors[2],
      },
      secondary: {
        main: fsColors.fsDarkBlue.colors[1],
        dark: fsColors.fsDarkBlue.colors[1],
        light: fsColors.fsDarkBlue.colors[3],
      },
      ...commonThemeOptions,
    },
  }

  const darkThemeOptions: ThemeOptions = {
    palette: {
      mode: 'dark',
      primary: {
        main: fsColors.fsDarkBlue.colors[2],
        dark: fsColors.fsDarkBlue.colors[2],
        light: fsColors.fsDarkBlue.colors[1],
      },
      secondary: {
        main: fsColors.fsDarkBlue.colors[1],
        dark: fsColors.fsDarkBlue.colors[1],
        light: fsColors.fsDarkBlue.colors[0],
      },
      ...commonThemeOptions,
    },
  }

  const customTheme = createTheme({
    ...(currentTheme === 'light' ? lightThemeOptions : darkThemeOptions),
    typography: () => {
      const pxToRem = (px: number) => `${px * 16 / htmlFontSize}rem`
      return {
        htmlFontSize,
        fontFamily: 'Raleway, Arial',
        body1: {
          fontSize: pxToRem(1),
          fontWeight: 500,
        },
        body2: {
          fontSize: pxToRem(0.875),
          fontWeight: 500,
        },
        h1: {
          fontSize: pxToRem(1.75),
          fontWeight: 500,
        },
        h2: {
          fontSize: pxToRem(1.5),
          fontWeight: 500,
        },
        h3: {
          fontWeight: 500,
          fontSize: pxToRem(1.188),
        },
        n1: {
          fontWeight: 500,
          fontSize: pxToRem(2.125),
          display: 'block',
          fontFamily: 'Raleway',
        },
        n2: {
          fontWeight: 500,
          fontSize: pxToRem(1.75),
          fontFamily: 'Raleway',
          display: 'block',
        },
        n3: {
          fontWeight: 500,
          fontSize: pxToRem(1.375),
          fontFamily: 'Raleway',
        },
        button: {
          fontWeight: 500,
        },
        subtitle1: {
          fontWeight: 500,
        },
        subtitle2: {
          fontWeight: 500,
        },
        caption: {
          fontWeight: 500,
        },
        overline: {
          fontWeight: 500,
        },
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: "Raleway";
            src: url(${RalewayMedium.ttf});
            font-weight: 400;
            font-style: normal;
          }

          @font-face {
            font-family: "RalewayItalic";
            src: url(${RalewayItalic.ttf});
            font-weight: 400;
            font-style: normal;
          }

          @font-face {
            font-family: "Raleway";
            src: url(${RalewayBold.ttf});
            font-weight: bold;
            font-style: normal;
          }

          @font-face {
            font-family: "RalewayBold";
            src: url(${RalewayBold.ttf});
            font-weight: 400;
            font-style: normal;
          }
        `,
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontFamily: 'Raleway',
            fontWeight: 500,
            fontSize: '0.875rem',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
          },
          contained: {
            color: currentTheme === 'light' ? '#ffffff' : '#000000',
            backgroundColor:
              currentTheme === 'light'
                ? fsColors.fsDarkBlue.colors[0]
                : fsColors.fsDarkBlue.colors[2],
            '&:hover': {
              backgroundColor:
                currentTheme === 'light'
                  ? fsColors.fsDarkBlue.colors[4]
                  : fsColors.fsDarkBlue.colors[1],
            },
          },
          outlined: {
            color:
              currentTheme === 'light'
                ? fsColors.fsDarkBlue.colors[0]
                : fsColors.fsDarkBlue.colors[2],
            borderColor:
              currentTheme === 'light'
                ? fsColors.fsDarkBlue.colors[0]
                : fsColors.fsDarkBlue.colors[2],
            '&:hover': {
              borderColor:
                currentTheme === 'light'
                  ? fsColors.fsDarkBlue.colors[4]
                  : fsColors.fsDarkBlue.colors[1],
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            color: theme.palette.text.primary,
            boxShadow: `0px 0px 2px ${theme.palette.action.disabled}`,
            borderRadius: 4,
            padding: '8px 16px'
          }),
          arrow: ({ theme }) => ({
            color:
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            fontSize: '1.5em',
            '&::before': {
              boxShadow: `0px 0px 2px ${theme.palette.action.disabled}`,
            },
          }),
          popper: {
            zIndex: 2205,
          },
        },
        defaultProps: {
          arrow: true,
        },
      },
    },
    spacing: (factor: number) => `${0.5 * factor}rem`,
  })
  // customTheme = responsiveFontSizes(customTheme)

  return (
    <ThemeProvider theme={customTheme}>
      <ErrorBoundary
        FallbackComponent={Fallback}
        onReset={() => {
          dispatch(logoutUser())
          navigate('/login')
        }}
      >
        {children}
        <Dialog
          open={!!askAccess}
          onClose={() => dispatch(removeAccessReq())}
          aria-labelledby="responsive-dialog-title"
        >
          <DialogContent sx={{ textAlign: 'center', mb: 2 }}>
            <Stack alignItems="flex-end">
              <IconButton onClick={() => dispatch(removeAccessReq())}>
                <CloseRounded />
              </IconButton>
            </Stack>
            <Typography variant="h6" mt={-4}>
              {currentLanguage === 'de' ? 'Jetzt testen!' : 'Try now!'}
            </Typography>
            <Stack mt={2} justifyContent="center" alignItems="center">
              {askAccess === 'fs' ? (
                currentTheme === 'light' ? (
                  <FSLogo height={40} width={195} />
                ) : (
                  <FSLogoDark height={40} width={195} />
                )
              ) : currentTheme === 'light' ? (
                <PTLogo height={40} width={265} />
              ) : (
                <PTLogoDark height={40} width={265} />
              )}
              <Typography variant="h3" color="text.primary">
                {t(`${askAccess}.tagline` as any)}
              </Typography>
              <ul style={{ textAlign: 'left', width: '100%' }}>
                <li>
                  <Typography color="text.primary">{t(`${askAccess}.tagline.1` as any)}</Typography>
                </li>
                <li>
                  <Typography color="text.primary">{t(`${askAccess}.tagline.2` as any)}</Typography>
                </li>
                <li>
                  <Typography color="text.primary">{t(`${askAccess}.tagline.3` as any)}</Typography>
                </li>
              </ul>
              <Stack direction="row" justifyContent="center" alignItems="center" mt={2}>
                <Button
                  variant="contained"
                  LinkComponent={Link}
                  target="_blank"
                  href={`https://www.ines-analytics.com/${currentLanguage === 'en' ? 'en/' : ''}${
                    askAccess === 'fs' ? 'fair-solution' : 'pay-transparency'
                  }`}
                >
                  {t('detailed.desc.learn.more')}
                </Button>
                <Button
                  variant="outlined"
                  sx={{ ml: 2 }}
                  LinkComponent={Link}
                  href={`mailto:support.${askAccess}@ines-analytics.com`}
                >
                  {currentLanguage === 'de' ? 'kontaktieren Sie uns' : 'Contact Us'}
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </Dialog>
      </ErrorBoundary>
    </ThemeProvider>
  )
}

export default FsTheme
